import api from './api';
import { ImageConverter } from '../components/patient-360/rx/utils/imageConverter';

const BASE_URL = '/api/rx';

// Collapse repeated fetches of the SAME Rx image so one slow/broken image (a 504
// at the gateway) can't generate a retry-storm. A single user produced 1660
// identical 504s (Sentry PHOX-GSC-PHASE2-39) because nothing remembered the image
// had just failed: every dialog re-open, drug reshuffle, and manual Retry click
// re-hit the gateway. These maps live at module scope so they outlive component
// remounts. Only FAILURES are cached (briefly) — a successful load is never
// memoized, so good images behave exactly as before; errors still propagate so
// callers' catch/error UI are unchanged.
const RX_IMAGE_FAILURE_TTL_MS = 60_000;
const rxImageInFlight = new Map<string, Promise<RxImage>>();
const rxImageFailures = new Map<string, { error: unknown; at: number }>();

// Same storm protection for the all-attachments list fetch (GP-1171): the
// June fix above covered only getRxImage, leaving its sibling a bare fetch.
const rxAttachmentsInFlight = new Map<string, Promise<RxPrescription[]>>();
const rxAttachmentsFailures = new Map<string, { error: unknown; at: number }>();

export interface RxPrescription {
  id: string;
  scriptImageContentId: string;
  rxNumber: string;
  originalRxNumber?: string;
  referenceNumber?: string;
  drugName: string;
  drugStrength: string;
  drugDIN: string;
  drugForm: string;
  fillDate: string;
  scanDate: string;
  sig: string;
  dispensedQuantity: number;
  authorizedQuantity: number;
  remainingQuantity: number;
  hasImage: boolean;
  contentType: string;
  compressionType: string;
  description: string;
  comment: string;
  pageCount: number;
  scriptImageType?: number;
  imageSequence?: number;
  isPatientSubmission: boolean;
  // Status fields
  status?: number; // Status code (1-8)
  rxAdjState?: number | null; // Kroll Rx.AdjState (0/1/5/6/7 = non-clean adjudication; see RxAdjStateBadge)
  statusDescription?: string; // Human-readable status
  statusCategory?: string; // Category for grouping
  canFill?: boolean; // Whether prescription can be filled
  inactive?: boolean; // Whether prescription is marked inactive
  isMistake?: boolean; // Whether prescription is marked as mistake
}

export interface RxImage {
  id: string;
  scriptImageContentId: string;
  content: string; // Base64 encoded image
  mimeType: string;
  compressionType: string;
  description: string;
  comment: string;
  scanDate: string;
  pageCount: number;
  isPatientSubmission: boolean;
}

export interface RxDetails {
  rxNumber: string;
  patientId: string;
  doctorId: string;
  drugId: string;
  drugName: string;
  drugStrength: string;
  drugDIN: string;
  drugForm: string;
  fillDate: string;
  dateWritten: string;
  expiryDate: string;
  sig: string;
  status: string;
  dispensedQuantity: number;
  authorizedQuantity: number;
  remainingQuantity: number;
  refillsRemaining: number;
}

/**
 * Pull the signature rows out of a signatures endpoint response. Tolerates the
 * envelope (`{success, data: {signatures}}`), the unwrapped `{signatures}` body
 * and a bare array, and never invents rows — anything else yields `[]`.
 */
const unwrapSignatures = (body: any): any[] => {
  if (Array.isArray(body)) return body;
  const payload = body?.data ?? body;
  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload?.signatures) ? payload.signatures : [];
};

class RxService {
  /**
   * Convert script image type number to label
   */
  getImageTypeLabel(scriptImageType?: number): string {
    switch (scriptImageType) {
      case 1:
        return 'Script Image';
      case 2:
        return 'Hard Copy';
      default:
        return 'Unknown';
    }
  }

  /**
   * Get list of prescriptions for a patient
   * @param filter - 'active' | 'inactive' | 'all' (default: 'active')
   */
  async getRxList(
    pharmacyId: string,
    patientId: string,
    limit?: number,
    filter: 'active' | 'inactive' | 'unfilled' | 'all' = 'active',
  ): Promise<RxPrescription[]> {
    try {
      const response = await api.get(
        `${BASE_URL}/${pharmacyId}/patient/${patientId}/prescriptions`,
        {
          params: { limit, filter },
        },
      );
      // Handle nested response structure
      const data = response.data?.data?.data || response.data?.data || response.data || [];
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Failed to fetch prescription list:', error);
      throw error;
    }
  }

  /**
   * Correct a missing/mislabeled image MIME type using the actual content bytes.
   *
   * Every Rx-image viewer (RxImagePanel, RxImageModal, downloadRxImage) routes on
   * `image.mimeType`: a PDF whose Kroll MIME is absent or non-PDF is treated as an
   * image and rendered in a broken <img>, so the script "never shows" on PV
   * (GP-1015 — Burnaby adaptation / emergency-supply PDFs). We re-derive the type
   * from the payload's magic number when Kroll's label is missing/generic, or when
   * the bytes are unmistakably a PDF but the label disagrees. When the bytes can't
   * be identified we keep Kroll's label untouched (no fabrication).
   */
  private normalizeImageMimeType(image: RxImage): RxImage {
    if (!image || typeof image.content !== 'string' || !image.content) return image;

    const declared = (image.mimeType || '').trim().toLowerCase();
    const isGeneric =
      !declared ||
      declared === 'application/octet-stream' ||
      declared === 'application/binary' ||
      declared === 'binary/octet-stream';

    const sniffed = ImageConverter.detectMimeTypeFromBase64(image.content);
    if (!sniffed) return image;

    const bytesArePdf = sniffed === 'application/pdf';
    if (isGeneric || (bytesArePdf && declared !== 'application/pdf')) {
      if (declared && declared !== sniffed) {
        // Not silent: surface the correction so a systemic Kroll mislabel is visible.
        console.warn(
          `Rx image MIME corrected: Kroll declared "${image.mimeType}" but the bytes are "${sniffed}" ` +
            `(scriptImageContentId=${image.scriptImageContentId})`,
        );
      }
      return { ...image, mimeType: sniffed };
    }
    return image;
  }

  /**
   * Get specific prescription image
   */
  async getRxImage(
    pharmacyId: string,
    patientId: string,
    scriptImageContentId: string,
  ): Promise<RxImage> {
    const url = `${BASE_URL}/${pharmacyId}/patient/${patientId}/image/${scriptImageContentId}`;

    // Short-circuit an image that just failed so repeated triggers (re-open,
    // drug-switch, Retry spam) don't re-hammer the gateway. After the TTL the
    // next call retries normally — handles a backend that has since recovered.
    const failure = rxImageFailures.get(url);
    if (failure && Date.now() - failure.at < RX_IMAGE_FAILURE_TTL_MS) {
      throw failure.error;
    }

    // De-dupe concurrent identical requests (burst from re-renders/effects).
    const existing = rxImageInFlight.get(url);
    if (existing) return existing;

    const request = (async () => {
      try {
        const response = await api.get(url);
        // Handle nested response structure
        const data = response.data?.data?.data || response.data?.data || response.data;
        rxImageFailures.delete(url); // clear any stale failure marker on success
        // GP-1015: correct a missing/mislabeled MIME (e.g. Burnaby adaptation PDFs)
        // so the viewers route PDFs to the <iframe> instead of a broken <img>.
        return this.normalizeImageMimeType(data);
      } catch (error) {
        rxImageFailures.set(url, { error, at: Date.now() });
        console.error('Failed to fetch prescription image:', error);
        throw error;
      } finally {
        rxImageInFlight.delete(url);
      }
    })();

    rxImageInFlight.set(url, request);
    return request;
  }

  /**
   * Get prescription details by Rx number
   */
  async getRxDetails(pharmacyId: string, rxNumber: string): Promise<RxDetails> {
    try {
      const response = await api.get(`${BASE_URL}/${pharmacyId}/details/${rxNumber}`);
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch prescription details:', error);
      throw error;
    }
  }

  /**
   * Get prescription script image by Rx number (checks both rxNumber and originalRxNumber)
   * Returns the first prescription with scriptImageType === 1 that matches
   */
  async getRxScriptImageByNumber(
    pharmacyId: string,
    patientId: string,
    rxNumber: string
  ): Promise<RxPrescription | null> {
    try {
      const response = await api.get(
        `${BASE_URL}/${pharmacyId}/patient/${patientId}/prescriptions/by-rx/${rxNumber}`,
        {
          params: {
            scriptImageType: 1, // Only Script Images
            includeOriginalRx: true, // Check originalRxNumber too
          },
        }
      );
      const data = response.data?.data || response.data;
      return data || null;
    } catch (error) {
      console.error(`Failed to fetch script image for Rx #${rxNumber}:`, error);
      return null;
    }
  }

  /**
   * Get ALL attachments (Script Images + Hard Copies) for a specific Rx number.
   * Returns array of all prescriptions with images for the Rx.
   *
   * GP-1171: THROWS on fetch failure (it used to swallow errors and return [],
   * which made a Kroll timeout indistinguishable from "this Rx genuinely has no
   * attachments"). Failures are memoized for RX_IMAGE_FAILURE_TTL_MS and
   * concurrent identical calls are de-duplicated — same storm protection as
   * getRxImage (Sentry PHOX-GSC-PHASE2-39). Successes are never cached.
   */
  async getAllRxAttachments(
    pharmacyId: string,
    patientId: string,
    rxNumber: string
  ): Promise<RxPrescription[]> {
    const url = `${BASE_URL}/${pharmacyId}/patient/${patientId}/prescriptions/all-attachments/${rxNumber}`;

    const failure = rxAttachmentsFailures.get(url);
    if (failure && Date.now() - failure.at < RX_IMAGE_FAILURE_TTL_MS) {
      throw failure.error;
    }

    const existing = rxAttachmentsInFlight.get(url);
    if (existing) return existing;

    const request = (async () => {
      try {
        const response = await api.get(url);
        const data = response.data?.data || response.data || [];
        rxAttachmentsFailures.delete(url);
        return Array.isArray(data) ? data : [];
      } catch (error) {
        rxAttachmentsFailures.set(url, { error, at: Date.now() });
        console.error(`Failed to fetch attachments for Rx #${rxNumber}:`, error);
        throw error;
      } finally {
        rxAttachmentsInFlight.delete(url);
      }
    })();

    rxAttachmentsInFlight.set(url, request);
    return request;
  }

  /**
   * Get all images for a specific Rx number
   */
  async getRxImagesByNumber(pharmacyId: string, rxNumber: string): Promise<RxImage[]> {
    try {
      const response = await api.get(`${BASE_URL}/${pharmacyId}/rx/${rxNumber}/images`);
      const images: RxImage[] = response.data.data || [];
      // GP-1015: correct missing/mislabeled MIMEs (Burnaby adaptation PDFs) here too.
      return images.map((image) => this.normalizeImageMimeType(image));
    } catch (error) {
      console.error('Failed to fetch prescription images:', error);
      throw error;
    }
  }

  /**
   * Convert base64 image to blob URL for display
   */
  createImageBlobUrl(base64Content: string, mimeType: string): string {
    try {
      // Remove data URL prefix if present
      const base64Data = base64Content.replace(/^data:image\/[a-z]+;base64,/, '');

      // Convert base64 to binary
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Failed to create image blob URL:', error);
      throw error;
    }
  }

  /**
   * Download prescription image
   */
  downloadRxImage(image: RxImage, rxNumber: string, fileName?: string): void {
    try {
      const blobUrl = this.createImageBlobUrl(image.content, image.mimeType);

      // Determine file extension
      let extension = '.jpg';
      if (image.mimeType.includes('tiff')) {
        extension = '.tiff';
      } else if (image.mimeType.includes('png')) {
        extension = '.png';
      } else if (image.mimeType.includes('pdf')) {
        extension = '.pdf';
      }

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || `Rx-${rxNumber}-${Date.now()}${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up blob URL
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Failed to download image:', error);
      throw error;
    }
  }

  /**
   * Both signature endpoints answer `{success, data: {signatures: [...]}}` — a FLAT
   * list of signature rows, each carrying its own `rxNumber`. NOT a map keyed by Rx.
   *
   * GP-1387: `getSignaturesBatch` used to declare `Promise<Record<string, any[]>>`
   * and hand the raw `{signatures: […]}` envelope back. Its only caller indexed it
   * as `sigs[rxNumber]` — always `undefined` — so every signature in the Verification
   * tab rendered blank while type-checking clean. Unwrap here, once, so callers can
   * only ever see the shape the controller actually returns.
   */
  async getSignatures(pharmacyId: string, rxNumber: string): Promise<any[]> {
    const response = await api.get(`${BASE_URL}/${pharmacyId}/signatures/${rxNumber}`);
    return unwrapSignatures(response.data);
  }

  async getSignaturesBatch(pharmacyId: string, rxNumbers: string[]): Promise<any[]> {
    if (!rxNumbers.length) return [];
    const response = await api.get(`${BASE_URL}/${pharmacyId}/signatures-batch`, {
      params: { rxNumbers: rxNumbers.join(',') },
    });
    return unwrapSignatures(response.data);
  }
}

export const rxService = new RxService();
